'use client'

import {
   Badge,
   Box,
   Button,
   Card,
   Container,
   Grid,
   Group,
   SimpleGrid,
   Stack,
   Text,
   ThemeIcon
} from '@blocks'
import {
   siteTypographySectionLeadFontSizeDesktop,
   siteTypographySectionLeadFontSizeMobile,
   siteTypographySectionTitleDisplayFontSizeDesktop,
   siteTypographySectionTitleDisplayFontSizeMobile,
   siteTypographySectionTitlePrimaryFontSizeDesktop,
   siteTypographySectionTitlePrimaryFontSizeMobile,
   siteTypographySectionTitleSecondaryFontSizeDesktop,
   siteTypographySectionTitleSecondaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {
   IconArrowRight,
   IconCar,
   IconCheck,
   IconDog,
   IconDroplets,
   IconSparkles,
   IconWind
} from '@icons'
import {useRouter} from 'next/navigation'

/** Service cards only. */
const servicesTileIdle = '#1d3e76'

const servicesTileHover = '#162f5c'

/** Richer sky tones than `siteAccentHexMuted` → `siteAccentHex` for navy service tiles. */
const serviceCallToActionGradientFrom = '#4db8e6'

const serviceCallToActionGradientTo = '#0c8ec9'

/** Filled CTA on white detailing card — deep navy. */
const detailingCallToActionBackgroundHex = '#0f3558'

const detailingCallToActionHoverBackgroundHex = '#143e68'

const detailingCallToActionButtonStyles = {
   root: {
      minHeight: 52,
      paddingLeft: 28,
      paddingRight: 28,
      paddingTop: 14,
      paddingBottom: 14,
      backgroundColor: detailingCallToActionBackgroundHex,
      color: '#ffffff',
      border: '1px solid rgba(15, 53, 88, 0.45)',
      boxShadow: '0 4px 18px rgba(15, 53, 88, 0.28)',
      fontWeight: 700,
      transition:
         'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
         backgroundColor: detailingCallToActionHoverBackgroundHex,
         borderColor: 'rgba(20, 62, 104, 0.55)',
         boxShadow: '0 8px 26px rgba(15, 53, 88, 0.35)'
      },
      '&:active': {
         transform: 'translateY(1px)'
      }
   }
}

/** Keeps service card description bands aligned; list sits flush underneath. */
const serviceDescriptionMinHeightPx = {
   mobile: 102,
   desktop: 128
} as const

const services = [
   {
      icon: IconCar,
      title: 'Automatic Car Wash',
      description:
         'Two automatic wash machines with 2.3m and 2.8m wash heights — quick, consistent results for everyday vehicles through to taller vans and utes.',
      items: [
         '2.3m wash height',
         '2.8m wash height',
         'Suitable for all vehicle sizes'
      ],
      href: '/car-wash#automatic'
   },
   {
      icon: IconDroplets,
      title: 'Self-Serve Wash Bays',
      description:
         'Full control over your wash: degreaser, pre-soak, foam brush, rinse, triple foam, wax, and more — take your time and finish the way you like.',
      items: [
         'Degreaser / tyre cleaner',
         'Pre-soak',
         'High-pressure soap',
         'Foam brush',
         'High-pressure rinse',
         'Triple foam conditioner',
         'High-pressure wax'
      ],
      href: '/car-wash#self-serve'
   },
   {
      icon: IconDog,
      title: 'Dog Wash',
      description:
         'Shampoo, conditioner, rinse, dryer, and disinfectant in one place — keep the mess out of your bathroom and your dog looking sharp.',
      items: [
         'Shampoo',
         'Flea treatment',
         'Conditioner',
         'Rinse',
         'Blow dryer',
         'Disinfectant'
      ],
      href: '/dog-wash'
   },
   {
      icon: IconWind,
      title: 'Vacuum Stations',
      description:
         'Fragramed and turbo vacuums on site so you can freshen the interior before you head home.',
      items: ['1 fragranced vacuum', '1 turbo vacuum'],
      href: '/car-wash#vacuum'
   }
]

const detailingItems = [
   'Full interior vacuum & deep clean',
   'Hand wash & exterior detail',
   'Wheels, tyres & trims cleaned',
   'Protective finish products',
   'Ideal for sale prep or full refresh'
]

export const Services = () => {
   const router = useRouter()
   const isMobile = useMediaQuery('(max-width: 768px)')

   return (
      <Box
         id={'services'}
         bg={'primary.9'}
         style={{
            backgroundColor: 'primary.9'
         }}
      >
         <Container
            py={isMobile ? 96 : 180}
            size={'xl'}
         >
            <Stack gap={isMobile ? 48 : 72}>
               <Stack
                  align={isMobile ? 'flex-start' : 'center'}
                  gap={isMobile ? 18 : 22}
               >
                  <Badge
                     c={'primary.9'}
                     fw={900}
                     tt={'uppercase'}
                     size={isMobile ? 'lg' : 'xl'}
                     color={'primary.0'}
                     variant={'filled'}
                  >
                     What we offer
                  </Badge>
                  <Text
                     c={'primary.3'}
                     fz={
                        isMobile
                           ? siteTypographySectionTitleDisplayFontSizeMobile
                           : siteTypographySectionTitleDisplayFontSizeDesktop
                     }
                     fw={800}
                     ta={isMobile ? 'left' : 'center'}
                     lh={1.08}
                     maw={800}
                  >
                     Everything you need,
                     <br />
                     one convenient stop
                  </Text>
                  <Text
                     c={'primary.0'}
                     fz={
                        isMobile
                           ? siteTypographySectionLeadFontSizeMobile
                           : siteTypographySectionLeadFontSizeDesktop
                     }
                     fw={500}
                     ta={isMobile ? 'left' : 'center'}
                     lh={1.7}
                     maw={isMobile ? '100%' : '65%'}
                  >
                     Automatic wash, self-serve bays, dog wash,
                     vacuums, and professional detailing — all at 42
                     Studholme Street, Morrinsville.
                  </Text>
               </Stack>

               <Grid gutter={{base: 20, sm: 28, md: 36}}>
                  {services.map((service) => (
                     <Grid.Col
                        key={service.title}
                        span={{base: 12, sm: 6}}
                        style={{display: 'flex'}}
                     >
                        <Card
                           w={'100%'}
                           p={isMobile ? 28 : 36}
                           ta={'left'}
                           bg={'#032749'}
                           mih={isMobile ? undefined : 300}
                           radius={isMobile ? 18 : 22}
                        >
                           <Stack
                              justify={'space-between'}
                              gap={isMobile ? 36 : 48}
                              style={{flex: 1, width: '100%'}}
                           >
                              <Stack gap={25}>
                                 <Group
                                    justify={'flex-start'}
                                    align={'center'}
                                    gap={isMobile ? 20 : 30}
                                    wrap={
                                       isMobile ? 'nowrap' : 'nowrap'
                                    }
                                 >
                                    <ThemeIcon
                                       size={isMobile ? 70 : 80}
                                       radius={12}
                                       variant={'filled'}
                                       color={'primary.1'}
                                       style={{
                                          color: 'tertiary.0',
                                          flexShrink: 0
                                       }}
                                    >
                                       <service.icon
                                          stroke={2}
                                          size={isMobile ? 40 : 50}
                                          color={'#032749'}
                                       />
                                    </ThemeIcon>
                                    <Text
                                       c={'primary.2'}
                                       fz={
                                          isMobile
                                             ? siteTypographySectionTitleSecondaryFontSizeMobile
                                             : siteTypographySectionTitleSecondaryFontSizeDesktop
                                       }
                                       fw={800}
                                       lh={1.25}
                                    >
                                       {service.title}
                                    </Text>
                                 </Group>
                                 <Text
                                    c={'primary.0'}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={500}
                                    lh={1.7}
                                    style={{
                                       minHeight: isMobile
                                          ? serviceDescriptionMinHeightPx.mobile
                                          : serviceDescriptionMinHeightPx.desktop
                                    }}
                                 >
                                    {service.description}
                                 </Text>
                                 <SimpleGrid
                                    cols={isMobile ? 1 : 2}
                                    spacing={isMobile ? 12 : 16}
                                    verticalSpacing={
                                       isMobile ? 10 : 10
                                    }
                                    style={{alignItems: 'flex-start'}}
                                 >
                                    {service.items.map((item) => (
                                       <Group
                                          key={item}
                                          align={'flex-start'}
                                          gap={8}
                                          wrap={'nowrap'}
                                          style={{minWidth: 0}}
                                       >
                                          <IconCheck
                                             size={isMobile ? 22 : 25}
                                             stroke={3}
                                             color={'#73b5f6'}
                                             style={{
                                                flexShrink: 0,
                                                marginTop: 4
                                             }}
                                          />
                                          <Text
                                             c={'primary.1'}
                                             fz={
                                                isMobile
                                                   ? siteTypographySectionLeadFontSizeMobile
                                                   : siteTypographySectionLeadFontSizeDesktop
                                             }
                                             fw={500}
                                             lh={1.45}
                                             style={{minWidth: 0}}
                                          >
                                             {item}
                                          </Text>
                                       </Group>
                                    ))}
                                 </SimpleGrid>
                              </Stack>
                              <Group>
                                 <Button
                                    c={'primary.9'}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={700}
                                    size={'lg'}
                                    color={'primary.0'}
                                    variant={'filled'}
                                    radius={'xl'}
                                    fullWidth={isMobile}
                                    rightSection={
                                       <IconArrowRight
                                          size={isMobile ? 22 : 26}
                                          stroke={2.2}
                                       />
                                    }
                                    onClick={() =>
                                       router.push(service.href)
                                    }
                                 >
                                    Explore
                                 </Button>
                              </Group>
                           </Stack>
                        </Card>
                     </Grid.Col>
                  ))}

                  <Grid.Col span={12}>
                     <Card
                        p={isMobile ? 28 : 48}
                        bg={'primary.8'}
                        radius={isMobile ? 18 : 22}
                     >
                        <Stack gap={50}>
                           <Group
                              justify={'flex-start'}
                              align={'center'}
                              gap={30}
                              wrap={isMobile ? 'wrap' : 'nowrap'}
                           >
                              <ThemeIcon
                                 size={isMobile ? 70 : 80}
                                 radius={'md'}
                                 color={'primary.1'}
                                 style={{
                                    color: 'white',
                                    flexShrink: 0
                                 }}
                              >
                                 <IconSparkles
                                    stroke={2}
                                    size={50}
                                    color={'#1c69c5'}
                                 />
                              </ThemeIcon>
                              <Text
                                 c={'white'}
                                 fz={
                                    isMobile
                                       ? siteTypographySectionTitlePrimaryFontSizeMobile
                                       : siteTypographySectionTitlePrimaryFontSizeDesktop
                                 }
                                 fw={800}
                                 lh={1.12}
                              >
                                 Clean Park Detailing & Grooming
                              </Text>
                           </Group>
                           <Group
                              gap={isMobile ? 20 : 150}
                              align={'flex-start'}
                              justify={'space-between'}
                              wrap={isMobile ? 'wrap' : 'nowrap'}
                           >
                              <Stack
                                 gap={isMobile ? 18 : 22}
                                 align={'flex-start'}
                                 ta={'left'}
                                 style={{width: '100%'}}
                              >
                                 <Text
                                    c={'primary.1'}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={500}
                                    lh={1.75}
                                 >
                                    For vehicles that need more than a
                                    standard wash. We restore,
                                    refresh, and protect your car
                                    inside and out — with
                                    professional-grade care and real
                                    attention to detail.
                                 </Text>
                              </Stack>
                              <Stack
                                 gap={isMobile ? 18 : 20}
                                 style={{
                                    width: '100%'
                                 }}
                              >
                                 {detailingItems.map((item) => (
                                    <Group
                                       key={item}
                                       align={'flex-start'}
                                       gap={14}
                                       wrap={'nowrap'}
                                    >
                                       <IconCheck
                                          size={isMobile ? 22 : 26}
                                          stroke={3.5}
                                          color={'white'}
                                       />
                                       <Text
                                          c={'primary.1'}
                                          fz={
                                             isMobile
                                                ? siteTypographySectionLeadFontSizeMobile
                                                : siteTypographySectionLeadFontSizeDesktop
                                          }
                                          fw={700}
                                          lh={1.2}
                                       >
                                          {item}
                                       </Text>
                                    </Group>
                                 ))}
                              </Stack>
                           </Group>
                           <Group
                              justify={'center'}
                              pt={isMobile ? 28 : 60}
                           >
                              <Button
                                 c={'primary.8'}
                                 fz={
                                    isMobile
                                       ? siteTypographySectionLeadFontSizeMobile
                                       : siteTypographySectionLeadFontSizeDesktop
                                 }
                                 fw={700}
                                 size={'xl'}
                                 radius={'xl'}
                                 color={'primary.0'}
                                 variant={'filled'}
                                 fullWidth={isMobile}
                                 rightSection={
                                    <IconArrowRight
                                       size={isMobile ? 22 : 26}
                                       stroke={2.2}
                                    />
                                 }
                                 onClick={() =>
                                    router.push('/detailing')
                                 }
                              >
                                 Book Now
                              </Button>
                           </Group>
                        </Stack>
                     </Card>
                  </Grid.Col>
               </Grid>
            </Stack>
         </Container>
      </Box>
   )
}
